import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import { Box, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useParams, useHistory } from "react-router";
import { Service } from "../../Service/ApiService";
import { CommonService } from "../../Service/CommonService";
import AlterImage from "../../assets/images/alter_image.png";
import StatusDropdown from "../Common/StatusDropdown";
import DataGridLoader from "../Common/DataGridLoader";
import { Dropdown, Form } from "react-bootstrap";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMore from '../../assets/images/icons/ExpandMore.svg';
import { SupportApiService } from "../../Service/SupportApiService";
import Constant from "../../Constant";

interface RecentRequestProps {
  onTotalCountChange?: (count: number) => void;
}

const RecentRequest: React.FC<RecentRequestProps> = ({ onTotalCountChange }) => {
  const history = useHistory();
  const [recentRequests, setRecentRequests] = useState<any[]>([]);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const isDashboard = location.pathname === "/master/dashboard";
  const [isLoading, setIsLoading] = useState(false);
  const [isSeller, setIsSeller] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState(0);
  const isAdmin = CommonService.isAdmin();
  const isWarehouseUser = CommonService.isWarehouse();
  const showCompanyInfo = isDashboard || isWarehouseUser;
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortField, setSortField] = useState("createdModifiedDate"); // Default sort
  const [sortDirection, setSortDirection] = useState("asc"); // Default ascending
  const [showReqType, setShowReqType] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const toggleDropdown = () => { setShowDropdown((prev) => !prev); }
  const divRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef(null);
  const controllerRef = useRef<AbortController | null>(null);
  const [services, setServices] = useState([
        { "id": 1, "serviceName": "Order Request" },
        { "id": 2, "serviceName": "Offer Request" }
  ]);
  const statusOptions = [
  { value: "PENDING_REVIEW", label: "Pending Review" },
  { value: "UNDER_REVIEW", label: "Under Review" },
  { value: "ACCEPTED", label: "Accepted" },
  { value: "UPLOADED", label: "Uploaded" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "CONTRACT_SENT", label: "Contract Sent" },
  { value: "CONTRACT_EXECUTED", label: "Contract Executed" },
  { value: "PUBLISHED", label: "Published" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "CLOSED", label: "Closed" },
  { value: "OWNERSHIP_TRANSFERRED", label: "Ownership Transferred" },
  { value: "FINALIZED", label: "Finalized" },
  { value: "SOLD", label: "Sold" },
  { value: "ASSIGNED", label: "Assigned" },
  { value: "DELIVERED", label: "Delivered" },
  { value: "UNPUBLISHED", label: "Unpublished" },
  { value: "REPUBLISHED", label: "Republished" },
  { value: "DELETED", label: "Deleted" },
  { value: "IN_TRANSIT", label: "In Transit" },
  { value: "ADMIN_ACCEPTED", label: "Admin Accepted" },
  { value: "ADMIN_REJECTED", label: "Admin Rejected" },
  { value: "INVOICE_SENT", label: "Invoice Sent" },
  { value: "COMPLETED", label: "Completed" },
  { value: "PAYMENT_RECEIVED", label: "Payment Received" },
  { value: "RE_PROCESSING", label: "Re Processing" },
  { value: "SEND_TO_OWNER", label: "Send to Owner" },
  { value: "SEND_TO_WAREHOUSE", label: "Send to Warehouse" },
  { value: "IN_PROCESS", label: "In Process" },
  { value: "REASSIGN_TO_ADMIN", label: "Reassign to Admin" },
  { value: "CONFIRMED_FOR_WAREHOUSE", label: "Confirmed for Warehouse" },
  { value: "IN_STAGE_FOR_WAREHOUSE", label: "In Stage for Warehouse" },
  { value: "ADMIN_ACCEPTED_FOR_WAREHOUSE", label: "Admin Accepted for Warehouse" },
  { value: "WAREHOUSE_COMPLETED", label: "Warehouse Completed" },
  { value: "CHECKED", label: "Checked" },
  { value: "CONFIRMED", label: "Confirmed" },
  { value: "IN_STAGE", label: "In Stage" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "SOURCE_ACCEPTED", label: "Source Accepted" },
  { value: "DESTINATION_ACCEPTED", label: "Destination Accepted" },
  { value: "SOURCE_REJECTED", label: "Source Rejected" },
  { value: "DESTINATION_REJECTED", label: "Destination Rejected" }
];

  const [filter, setFilter] = useState<any>({
        requestType: null,
        requestNumber: null,
        companyName: null,
        createdBy: null,
        email: null,
        phoneNumber: null,
        modifiedDate: null,
        status: null,
        modifiedOn: null
    });

  const fetchRecentRequestCount = async (page: number, size: number, filter: any, sortField: any, sortDirection: any) => {
    const controller = new AbortController();
    const signal = controller.signal;
    try {
      setIsLoading(true);
      setRecentRequests([]);

      // Cancel the previous request if any
      if (controllerRef.current) {
          controllerRef.current.abort();
      }

      // Store the current controller to cancel the request if needed
      controllerRef.current = controller;
      // Important: Adjust the page number for backend (if it uses 0-based indexing)
      const response: any = await Service.fetchRecentRequestCount(
        CommonService.isUser() ? CommonService.getCompanyId() : id,
        page,
        size,
        filter,
        sortField,
        sortDirection,
        CommonService.getDashboardPrefrence(),
        signal
      );
      setRecentRequests(response.data.content);
      setTotalCount(response.data.totalElements);
      onTotalCountChange?.(response.data.totalElements);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching data:", error);
    }
  };

const fetchAllServices = async () => {
    try {
        setIsLoading(true);

        const response = await SupportApiService.getAllServiceType();

        let filteredServices = response.data;

        // Hide Barrel Valuation for Warehouse users
        if (CommonService.isWarehouse()) {
            filteredServices = response.data.filter(
                (service) =>
                    service.serviceName !== Constant.CustomerService.BARREL_VALUATION
            );
        }

        const updatedServices = [
            ...services,
            ...filteredServices
        ];

        setServices(updatedServices);

    } catch (error) {
        console.log("Error occurred while API calling:", error);
    } finally {
        setIsLoading(false);
    }
};

  useEffect(() => {
    // Fetch initial data when component mounts or route changes
    fetchRecentRequestCount(page, pageSize, filter, sortField, sortDirection);
    fetchAllServices();
  }, [location.pathname]);

  const handleRowClick = (params: any) => {
    const requestId = params?.row?.requestId;
    const requestType = params?.row?.requestType;

    if (!requestId) {
      console.warn("Invalid request ID");
      return;
    }

    const linkPrefix = CommonService.isAdmin() ? "/master" : "/user";

    if (CommonService.isWarehouse()) {
      history.push(`/master/view-customer-service-request/${requestId}`);
      return;
    }

    let url = "";

    if (requestType === "Order Request") {
      url = `${linkPrefix}/order-detail/${requestId}`;
    } else if (requestType === "Offer Request") {
      url = `${linkPrefix}/view-offer-request/${requestId}`;
    } else if (requestType === "Sample Request" && params?.row?.inventoryId != null) {
      url = `${linkPrefix}/view-sample-request/${requestId}`;
    } else {
      url = `${linkPrefix}/view-customer-service-request/${requestId}`;
    }

    history.push(url, {requestId: requestId, requestType: requestType});
  };

  function formatStatus(status: string): string {
    const formattedStatus = status
      .split(/[_\\]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

    return formattedStatus;
  }

  const CellWithTooltip = ({ value }: { value: string | number }) => {
    return (
      <Tooltip arrow title={value?.toString()}>
        <span>{value?.toString()}</span>
      </Tooltip>
    );
  };

  const localeText = {
    noRowsLabel: isLoading ? "Fetching records ..." : "No Record Found",
  };

  // Filter rows based on search query
  const filteredRows = recentRequests.filter(
    (row) =>
      row.name?.toLowerCase().includes(search?.toLowerCase()) ||
      row.email?.toLowerCase().includes(search?.toLowerCase()) ||
      row.city?.toLowerCase().includes(search?.toLowerCase())
  );

  const tableDataWithIds = recentRequests.map((rowData, index) => ({
    ...rowData,
    id: index + 1, // Assigning a unique ID based on the index
  }));

  const handleSelect = (event, value) => {
    event.stopPropagation(); // stop parent click
    handleFilterChange({ target: { value } }, "requestType");
    setShowReqType(false); // ✅ close after selecting
  };

  const handleFilterChange = (event: any, field: keyof any) => {
    const value = event.target.value;

    const tempFilter = {
        ...filter,
        [field]: value
    };

    // Update the filter state
    setFilter(tempFilter);

    fetchRecentRequestCount(page, pageSize, tempFilter, sortField, sortDirection);
  };

  const handleStatusChange = (event: any, value: string, status: string | null) => {
      const isChecked = event.target.checked;

      // Convert the status string to an array
      const statusArray = status ? status.split(",") : [];

      // Update the status list based on checkbox state
      const updatedStatusArray = isChecked
          ? [...statusArray, value] // Add status if checked
          : statusArray.filter((s) => s !== value); // Remove status if unchecked

      // Convert array back to a comma-separated string
      const updatedStatus = updatedStatusArray?.length > 0 ? updatedStatusArray.join(",") : null;

      // Update the filter state with the modified status string
      setFilter({ ...filter, status: updatedStatus });

      // Reload service requests with updated filters
      fetchRecentRequestCount(page, pageSize, { ...filter, status: updatedStatus }, sortField, sortDirection);
  };

  const columns: GridColDef[] = [
    {
      field: "requestType",
      headerName: "Request Type",
      headerClassName: "table-header border-0 my-2 h-100",
      flex: 1,
      minWidth:250,
      sortable: true,
      renderHeader: (params) => (
        <>
          <div>
            <Typography
              variant="body2"
              sx={{ color: "#6F6F6F", fontSize: "14px" }}
            >
              {params.colDef.headerName}
            </Typography>
            <div
              ref={dropdownRef}
              className="position-relative"
              style={{ width: "200px" }}
              onClick={(e) => e.stopPropagation()} // prevent bubbling to parents
            >
              <Dropdown show={showReqType}>
                <Dropdown.Toggle
                  variant="light"
                  className="w-100 border mt-1 form-select custom-dropdown-toggle text-start"
                  id="dropdown-custom-select"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowReqType((prev) => !prev); // ✅ toggle open/close
                  }}
                >
                  {filter.requestType || "Select Request Type"}
                </Dropdown.Toggle>

                {showReqType && (
                  <Dropdown.Menu className="custom-dropdown-menu w-100 show">
                    {services.map((service) => (
                      <Dropdown.Item
                        key={service.id}
                        onClick={(event) => handleSelect(event, service.serviceName)}
                        active={filter.requestType === service.serviceName}
                      >
                        {service.serviceName}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                )}
              </Dropdown>
            </div>
          </div>
        </>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.row.requestType} />
      ),
    },
    {
      field: "requestNumber",
      headerName: "Request Number",
      headerClassName: "table-header border-0 my-2 h-100",
      flex: 1,
      minWidth:250,
      sortable: true,
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "200px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              //value={filter.requestNumber}
              defaultValue={filter.requestNumber}
              onChange={(event) => handleFilterChange(event, "requestNumber")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.row.requestNumber || "-"} />
      ),
    },
    showCompanyInfo && {
      field: "companyName",
      headerName: "Company Name",
      headerClassName: "table-header border-0 my-2 h-100",
      flex: 1,
      minWidth:250,
      sortable: true,
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "200px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              //value={filter.companyName}
              defaultValue={filter.companyName}
              onChange={(event) => handleFilterChange(event, "companyName")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => <CellWithTooltip value={params.value} />,
    },
    ...((isAdmin || isWarehouseUser) ? [
    {
      field: "createdBy",
      headerName: "User",
      flex: 1,
      minWidth:250,
      sortable: true,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "200px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              //value={filter.createdBy}
              defaultValue={filter.createdBy}
              onChange={(event) => handleFilterChange(event, "createdBy")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <div className="d-flex gap-2 align-items-center">
          <div className="user-img-wrapper">
            <img src={AlterImage} alt="CompanyIcn" height={40} width={40}/>
          </div>
          <span>
            <CellWithTooltip value={params.value} />
          </span>
        </div>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      headerClassName: "table-header border-0 my-2 h-100",
      flex: 1,
      minWidth:250,
      sortable: true,
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "200px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              //value={filter.email}
              defaultValue={filter.email}
              onChange={(event) => handleFilterChange(event, "email")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => <CellWithTooltip value={params.value} />,
    },
    {
      field: "phoneNumber",
      headerName: "Phone",
      headerClassName: "table-header border-0 my-2 h-100",
      flex: 1,
      minWidth:250,
      sortable: true,
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "200px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              //value={filter.phoneNumber}
              defaultValue={filter.phoneNumber}
              onChange={(event) => handleFilterChange(event, "phoneNumber")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => <CellWithTooltip value={params.value} />,
    }] 
    : []),
    {
      field: "modifiedDate",
      headerName: "Modified At",
      headerClassName: "table-header border-0 my-2 h-100",
      flex: 1,
      minWidth:250,
      sortable: true,
      valueGetter: (value: any, row: any) => {
        const dateTimeString = row.modifiedDate ? row.modifiedDate : null;
        if (dateTimeString) {
          return CommonService.isUser() &&
            !CommonService.isSameCompany(row.companyId)
            ? CommonService.formatStrictDate(dateTimeString)
            : CommonService.formatDateTime(dateTimeString);
        }
      },
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "200px" }}>
            <input
              type="date"
              className="form-control border mt-1"
              style={{ boxShadow: "none" }}
              id="modifiedOn"
              name="modifiedOn"
              placeholder="MM/DD/YYYY"
              //value={filter.modifiedOn}
              defaultValue={filter.modifiedOn}
              onChange={(event) => handleFilterChange(event, "modifiedOn")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => <CellWithTooltip value={params.value} />,
    },
    {
      field: "status",
      headerName: "Status",
      headerClassName: "table-header border-0 my-2 h-100",
      flex: 1,
      minWidth:300,
      sortable: true,
      renderHeader: (params) => (
        <>
          <div>
            <Typography
              variant="body2"
              sx={{ color: "#6F6F6F", fontSize: "14px" }}
            >
              {params.colDef.headerName}
            </Typography>

            <div className="position-relative" style={{ width: "210px" }}>
              <div
                className="form-control mb-1"
                style={{
                  cursor: "pointer",
                  borderBottomColor: "#D1D1D1",
                  marginTop: "4px",
                  background: "#fff",
                  padding: "5px",
                  overflowX: "hidden",
                  paddingRight: "16px",
                  textOverflow: "ellipsis",
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleDropdown();
                }}
                ref={buttonRef}
              >
                {filter.status?.length > 0 ? CommonService.formatStatus(filter.status).join(", ") : "Select Status"}
                <img src={ExpandMore} className="position-absolute top-50 end-0 translate-middle" />
              </div>
              {/* Custom Dropdown */}
              {showDropdown && (
                <div
                  className="dropdown-menu show status-multiselect-dropdown"
                  onClick={(event) => event.stopPropagation()}
                  style={{ whiteSpace: "wrap" }}
                  ref={divRef}
                >
                  {statusOptions.map((option) => (
                    <Form.Check
                      key={option.value}
                      id={`status-${option.value}`}
                      type="checkbox"
                      label={option.label}
                      value={option.value}
                      checked={filter.status?.split(",").includes(option.value) || false}
                      onChange={(event) => {
                        event.stopPropagation();
                        handleStatusChange(event, option.value, filter.status);
                      }}
                      className="cursor-pointer"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      ),
      renderCell: (params: any) => (
        <div
          className="cell-with-dropdown mb-2"
          onClick={(e) => e.stopPropagation()}
        >
          <StatusDropdown
            currentStatus={formatStatus(params.value)}
            onStatusChange={(newStatus) => {}}
            disableCondition={true}
            allowedLabels={[]}
          />
          {(params.row.status === "REJECTED" || params.row.status === "CANCELLED") && params.row.rejectionNote && (
            <Tooltip
              title={`Rejection Note: ${params.row.rejectionNote}`}
              arrow
            >
              <StickyNote2Icon
                className="note-icon cursor-pointer"
                style={{ color: "gray" }}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ].filter(Boolean) as GridColDef[];

  const onPaginationModelChange = (newPaginationModel) => {
    setPage(newPaginationModel.page);
    setPageSize(newPaginationModel.pageSize);

    // Fetch using the latest sorting values
    fetchRecentRequestCount(newPaginationModel.page, newPaginationModel.pageSize, filter, sortField, sortDirection);
  };

  const handleSortChange = (sortModel) => {
    if (sortModel.length > 0) {
        const newSortField = sortModel[0].field;
        const newSortDirection = sortModel[0].sort; // "asc" or "desc" directly from DataGrid
        
        setSortField(newSortField);
        setSortDirection(newSortDirection);
    
        fetchRecentRequestCount(page, pageSize, filter, newSortField, newSortDirection);
    }
  };  

  return (
    <>
      {CommonService.isWarehouse() ? (
        <div className="usersList p-0" style={{ paddingBottom: CommonService.isAdmin() ? 51 : "" }}>
          <div className="usersList__header--wrapper d-flex flex-wrap align-items-center">
            <div className="header--title me-2">
              <h5 className="mb-0"><b>Recent Requests</b></h5>
            </div>
          </div>
          <div className="user-table-container">
            <div className="userTable">
              <DataGrid
                localeText={localeText}
                rows={tableDataWithIds}
                columns={columns}
                className="data-grid support-req--listing"
                paginationModel={{ page, pageSize }}
                pagination
                paginationMode="server"
                sortingMode="server"
                rowCount={totalCount}
                onSortModelChange={handleSortChange}
                onPaginationModelChange={onPaginationModelChange}
                disableRowSelectionOnClick
                getRowId={(row) => row.requestId}
                loading={isLoading}
                slots={{
                  loadingOverlay: DataGridLoader,
                }}
                pageSizeOptions={[10, 20, 50, 100]}
                disableColumnResize
                onRowClick={handleRowClick}
                sx={{
                  "& .MuiDataGrid-row": {
                    cursor: "pointer",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              />
            </div>
          </div>
        </div>
      )
      : (
          <div className="custom-table-outter">
            <div className="dashboard__inner mx-0 gap-0">
              <div className="d-flex justify-content-between mb-3 ps-0">
                <h5 className="mb-0">
                  <b>Recent Requests</b>
                </h5>
              </div>
              <div className="table-responsive ps-0">
                <Box sx={{ height: 350, width: "100%" }}>
                  <DataGrid
                    localeText={localeText}
                    rows={tableDataWithIds}
                    columns={columns}
                    className="data-grid dashboard--listing support-req--listing"
                    paginationModel={{ page, pageSize }}
                    pagination
                    paginationMode="server"
                    sortingMode="server"
                    rowCount={totalCount}
                    onSortModelChange={handleSortChange}
                    onPaginationModelChange={onPaginationModelChange}
                    disableRowSelectionOnClick
                    getRowId={(row) => row.requestId}
                    loading={isLoading}
                    slots={{
                      loadingOverlay: DataGridLoader,
                    }}
                    pageSizeOptions={[10, 20, 50, 100]}
                    disableColumnResize
                    onRowClick={handleRowClick}
                    sx={{
                      "& .MuiDataGrid-row": {
                        cursor: "pointer",
                      },
                      "& .MuiDataGrid-row:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  />
                </Box>
              </div>
            </div>
          </div>
      )}
    </>
  );
};

export default RecentRequest;
